import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

type TimelineEntryProps = {
  readonly createdAt: Date;
  readonly itemContent: null | string;
  readonly processTitle: string;
  readonly type: string;
  readonly workspaceTitle: string;
};

const getDotSize = (type: string): number => {
  if (type === 'COMMENT_ADDED' || type === 'COMMENT_EDITED') {
    return 12;
  }

  if (
    type === 'TODO_COMPLETED' ||
    type === 'TODO_ADDED' ||
    type === 'FILE_ADDED'
  ) {
    return 8;
  }

  return 6;
};

const describeEvent = (type: string, content: null | string): string => {
  switch (type) {
    case 'ARCHIVED':
      return 'Archived';
    case 'COMMENT_ADDED':
      return 'Added comment';
    case 'COMMENT_EDITED':
      return 'Edited comment';
    case 'FILE_ADDED':
      return 'Added file';
    case 'MOVED':
      return 'Moved to workspace';
    case 'PINNED':
      return 'Pinned';
    case 'PROCESS_CREATED':
      return 'Process created';
    case 'SNOOZED':
      return 'Snoozed';
    case 'TODO_ADDED':
      return 'Added todo';
    case 'TODO_COMPLETED':
      return content ? `Completed: ${content}` : 'Completed todo';
    case 'TODO_UNCOMPLETED':
      return 'Marked todo incomplete';
    case 'UNARCHIVED':
      return 'Unarchived';
    case 'UNPINNED':
      return 'Unpinned';
    case 'UNSNOOZED':
      return 'Unsnoozed';
    default:
      return type.toLowerCase().replaceAll('_', ' ');
  }
};

const isCommentType = (type: string) =>
  type === 'COMMENT_ADDED' || type === 'COMMENT_EDITED';

export const TimelineEntry = ({
  createdAt,
  itemContent,
  processTitle,
  type,
  workspaceTitle,
}: TimelineEntryProps) => {
  const dotSize = getDotSize(type);
  const description = describeEvent(type, itemContent);
  const showCommentPreview = isCommentType(type) && itemContent;

  return (
    <div className="flex gap-3 py-2.5 border-b border-black/5 dark:border-white/5 last:border-0">
      {/* Dot + line */}
      <div className="flex flex-col items-center pt-0.5 flex-shrink-0">
        <div
          className="rounded-full bg-sky-500 dark:bg-sky-400 flex-shrink-0"
          style={{ height: dotSize, width: dotSize }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-black dark:text-white leading-tight truncate">
          {processTitle}
        </div>
        <div className="text-xs text-black/40 dark:text-white/40 mt-0.5">
          <span>{workspaceTitle}</span>
          <span className="mx-1">·</span>
          <span>{dayjs(createdAt).format('HH:mm')}</span>
        </div>
        <div className="text-xs text-black/60 dark:text-white/60 mt-0.5">
          {description}
        </div>
        {showCommentPreview && (
          <div className="mt-1 text-xs text-black/50 dark:text-white/50 italic line-clamp-2 bg-black/5 dark:bg-white/5 rounded px-2 py-1">
            {itemContent}
          </div>
        )}
      </div>
    </div>
  );
};
